import { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMembership } from '@/hooks/useMembership';
import { useAuth } from '@/hooks/useAuth';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Lock, Sparkles, Crown, ArrowRight, LogIn, LayoutDashboard, FileText } from 'lucide-react';

interface PaywallGuardProps {
  children: ReactNode;
  /**
   * Require a paid subscription specifically (not just credits)
   */
  requirePaidPlan?: boolean;
  /**
   * Feature name to display in the paywall message
   */
  feature?: string;
  /**
   * Custom message to show
   */
  message?: string;
  /**
   * Show inline instead of full-page block
   */
  variant?: 'full' | 'inline' | 'minimal';
}

/**
 * Guards content behind a paywall.
 * Shows an upgrade prompt if user doesn't have access.
 */
export function PaywallGuard({
  children,
  requirePaidPlan = false,
  feature,
  message,
  variant = 'full'
}: PaywallGuardProps) {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { canAccessTools, isPaid, planName, isLoading } = useMembership();

  // Show loading state
  if (authLoading || isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Not logged in
  if (!user) {
    if (variant === 'minimal') {
      return (
        <div className="p-4 text-center">
          <p className="text-muted-foreground mb-2">Please log in to continue</p>
          <Button size="sm" onClick={() => navigate('/auth')}>
            <LogIn className="w-4 h-4 mr-2" />
            Log In
          </Button>
        </div>
      );
    }

    return (
      <div className="flex items-center justify-center min-h-[400px] p-8">
        <Card className="max-w-md w-full text-center">
          <CardHeader>
            <div className="mx-auto w-16 h-16 bg-muted rounded-full flex items-center justify-center mb-4">
              <LogIn className="w-8 h-8 text-muted-foreground" />
            </div>
            <CardTitle>Sign In Required</CardTitle>
            <CardDescription>
              Please log in or create an account to access this feature.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Button className="w-full" onClick={() => navigate('/auth')}>
              Log In or Sign Up
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => navigate('/')}>
                <LayoutDashboard className="w-4 h-4 mr-2" />
                Dashboard
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Check if access is allowed
  const hasAccess = requirePaidPlan ? isPaid : canAccessTools;

  if (hasAccess) {
    return <>{children}</>;
  }

  // Show upgrade prompt
  const defaultMessage = requirePaidPlan
    ? 'This feature requires an active subscription.'
    : 'You need credits to use this feature. Upgrade your plan or purchase credits.';

  if (variant === 'minimal') {
    return (
      <div className="p-4 bg-primary/5 rounded-lg border border-primary/20 text-center">
        <Lock className="w-5 h-5 mx-auto text-primary mb-2" />
        <p className="text-sm text-muted-foreground mb-3">
          {message || defaultMessage}
        </p>
        <div className="flex flex-col gap-2">
          <Button size="sm" onClick={() => navigate('/pricing')}>
            <Sparkles className="w-4 h-4 mr-2" />
            Upgrade
          </Button>
          <Button size="sm" variant="outline" onClick={() => navigate('/')}>
            <LayoutDashboard className="w-4 h-4 mr-2" />
            Dashboard
          </Button>
        </div>
      </div>
    );
  }

  if (variant === 'inline') {
    return (
      <Card className="border-primary/20 bg-gradient-to-br from-primary/5 to-accent/5">
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-primary/20 rounded-xl flex-shrink-0">
              <Lock className="w-6 h-6 text-primary" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="font-semibold">Upgrade Required</h3>
                <Badge variant="secondary">{planName} Plan</Badge>
              </div>
              <p className="text-sm text-muted-foreground mb-4">
                {message || defaultMessage}
              </p>
              {feature && (
                <p className="text-sm text-muted-foreground mb-4">
                  <Sparkles className="inline w-4 h-4 mr-1 text-primary" />
                  Unlock: <strong className="text-foreground">{feature}</strong>
                </p>
              )}
              <div className="flex flex-wrap gap-2">
                <Button onClick={() => navigate('/pricing')}>
                  View Upgrade Options
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
                <Button variant="outline" onClick={() => navigate('/')}>
                  <LayoutDashboard className="w-4 h-4 mr-2" />
                  Dashboard
                </Button>
                <Button variant="outline" onClick={() => navigate('/tailored-resume-library')}>
                  <FileText className="w-4 h-4 mr-2" />
                  My Resumes
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Full variant
  return (
    <div className="flex items-center justify-center min-h-[400px] p-8">
      <Card className="max-w-lg w-full text-center border-primary/20 overflow-hidden">
        <div className="bg-gradient-to-br from-primary/10 via-primary/5 to-accent/10 p-8">
          <div className="mx-auto w-20 h-20 bg-gradient-to-br from-primary to-accent rounded-full flex items-center justify-center mb-4">
            <Crown className="w-10 h-10 text-primary-foreground" />
          </div>
          <Badge variant="outline" className="mb-4">{planName} Plan</Badge>
          <CardTitle className="text-2xl mb-2">
            {feature ? `Unlock ${feature}` : 'Upgrade to Continue'}
          </CardTitle>
          <CardDescription className="text-base">
            {message || defaultMessage}
          </CardDescription>
        </div>
        
        <CardContent className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div className="p-3 rounded-lg bg-muted">
              <p className="font-medium">More Credits</p>
              <p className="text-muted-foreground">Up to 500/month</p>
            </div>
            <div className="p-3 rounded-lg bg-muted">
              <p className="font-medium">Premium Features</p>
              <p className="text-muted-foreground">All tools unlocked</p>
            </div>
          </div>
          
          <Button size="lg" className="w-full" onClick={() => navigate('/pricing')}>
            <Sparkles className="w-4 h-4 mr-2" />
            View Upgrade Options
          </Button>
          
          <div className="flex gap-2 justify-center">
            <Button variant="outline" onClick={() => navigate('/')}>
              <LayoutDashboard className="w-4 h-4 mr-2" />
              Dashboard
            </Button>
            <Button variant="outline" onClick={() => navigate('/tailored-resume-library')}>
              <FileText className="w-4 h-4 mr-2" />
              My Resumes
            </Button>
          </div>
          
          <p className="text-xs text-muted-foreground">
            Plans start at $19/month. Cancel anytime.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

export default PaywallGuard;
